import React, { useState, useEffect, useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Card, CardContent, CardHeader } from "../../ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  MagnifyingGlass,
  Funnel,
  Check,
  CalendarBlank,
  Buildings,
  GraduationCap,
  CaretLeft,
  CaretRight,
  UserPlus,
  Hash,
  Briefcase,
  ArrowsLeftRight,
  Clock,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useGetInterns,
  getVacancies,
  useGetAllDepartments,
} from "../../../adapters/Requests";
import ExportDropdown from "../../export";
import { DateRangeFilter } from "../../ui/daterangefilter";
import { Sheet, SheetContent } from "../../ui/sheet";
import AssignSupervisorViewer from "../../viewers/AssignSupervisorViewer";
import { formatDuration } from "../../../adapters/utils";

export default function InternsTable() {
  const navigate = useNavigate();
  const location = useLocation();

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  const [viewMode, setViewMode] = useState("UNASSIGNED");
  const [selectedItType, setSelectedItType] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  // State for Sheet
  const [selectedInternId, setSelectedInternId] = useState(null);

  const [uniqueItTypes, setUniqueItTypes] = useState([]);
  const [uniqueBatches, setUniqueBatches] = useState([]);

  const { data, isLoading, isError, refetch } = useGetInterns({
    limit: pagination.pageSize,
    page: pagination.pageIndex + 1,
    ittype: selectedItType,
    batch: selectedBatch,
    search: searchQuery,
    status: viewMode,
    start_date: dateRange.from,
    end_date: dateRange.to,
  });

  const { data: vacanciesData } = getVacancies();
  const { data: departmentsData } = useGetAllDepartments();

  // Create departments lookup map
  const departmentsMap = useMemo(() => {
    const map = {};
    if (departmentsData?.departments) {
      departmentsData.departments.forEach((dept) => {
        map[dept.code] = dept.name;
      });
    }
    return map;
  }, [departmentsData]);

  useEffect(() => {
    const searchParams = new URLSearchParams();
    searchParams.set("view", viewMode);
    if (pagination.pageIndex > 0)
      searchParams.set("page", pagination.pageIndex + 1);
    if (selectedItType) searchParams.set("ittype", selectedItType);
    if (selectedBatch) searchParams.set("batch", selectedBatch);
    if (searchQuery) searchParams.set("search", searchQuery);
    if (dateRange.from) searchParams.set("from", dateRange.from);
    if (dateRange.to) searchParams.set("to", dateRange.to);

    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  }, [
    pagination.pageIndex,
    selectedItType,
    selectedBatch,
    searchQuery,
    viewMode,
    dateRange,
    navigate,
    location.pathname,
  ]);

  useEffect(() => {
    if (vacanciesData && vacanciesData.length > 0) {
      setUniqueItTypes(
        Array.from(
          new Set(
            vacanciesData.map((item) =>
              item.vacancy_position.name.toUpperCase(),
            ),
          ),
        ),
      );
      setUniqueBatches(
        Array.from(new Set(vacanciesData.map((item) => item.batch.name))),
      );
    }
  }, [vacanciesData]);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setSearchQuery(searchTerm);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  };

  const handleRowClick = (id) => {
    // UPDATED: Open Sheet instead of navigating
    setSelectedInternId(id);
  };

  // Helper function to get department name from code
  const getDepartmentName = (intern) => {
    const deptCode =
      intern.application?.Applicant?.InternshipProgram?.[0]?.department;

    // First try to look up the department name from our departments map
    if (deptCode && departmentsMap[deptCode]) {
      return departmentsMap[deptCode];
    }

    // If deptCode doesn't look like a code (not all digits), it might already be a name
    if (deptCode && !/^\d+$/.test(deptCode)) {
      return deptCode;
    }

    // Fallback: show "Pending" if no department assigned
    return "Pending";
  };

  const columns = [
    {
      header: "Intern Details",
      accessorKey: "fullName",
      cell: ({ row }) => (
        <div className="flex flex-col py-1 max-w-[180px]">
          <span className="font-bold text-gray-900 text-sm truncate">
            {row.original.fullName}
          </span>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-gray-500 font-mono flex items-center bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">
              <Hash size={10} className="mr-0.5" /> {row.original.code}
            </span>
          </div>
        </div>
      ),
    },
    ...(viewMode === "UNASSIGNED"
      ? [
          {
            header: "Program Info",
            accessorKey: "batch",
            cell: ({ row }) => (
              <div className="flex flex-col gap-1">
                <div className="flex items-center text-gray-700 text-xs font-medium">
                  <CalendarBlank className="mr-1.5 text-gray-400" size={14} />
                  {row.original.batch}
                </div>
                <div className="flex items-center text-gray-500 text-[11px]">
                  <Clock className="mr-1.5 text-gray-400" size={14} />
                  {row.original.duration}
                </div>
              </div>
            ),
          },
        ]
      : []),
    {
      header: "Education",
      accessorKey: "course",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1 max-w-[180px]">
          <div className="flex items-center text-gray-700 text-xs font-semibold truncate">
            <GraduationCap
              className="mr-1.5 text-gray-400 shrink-0"
              size={14}
            />
            <span className="truncate" title={row.original.cos}>
              {row.original.cos}
            </span>
          </div>
          <div className="flex items-center text-gray-500 text-[11px] truncate">
            <Buildings className="mr-1.5 text-gray-400 shrink-0" size={14} />
            <span className="truncate" title={row.original.school}>
              {row.original.school}
            </span>
          </div>
        </div>
      ),
    },
    {
      header: "Department",
      accessorKey: "department",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center text-gray-700 text-xs font-medium">
            <Briefcase className="mr-1.5 text-gray-400" size={14} />
            {row.original.department}
          </div>
        </div>
      ),
    },
    {
      header: "Joined",
      accessorKey: "created_at",
      cell: ({ row }) => (
        <div className="text-xs text-gray-500 font-mono">
          {new Date(row.original.created_at).toLocaleDateString()}
        </div>
      ),
    },
    ...(viewMode === "ASSIGNED"
      ? [
          {
            header: "Attendance",
            accessorKey: "attendance",
            cell: ({ row }) => (
              <div className="flex items-center gap-2 text-xs">
                <div className="flex flex-col items-center px-2 py-1 bg-green-50 rounded border border-green-100">
                  <span className="font-bold text-green-700">
                    {row.original.attendance}
                  </span>
                  <span className="text-[9px] text-green-600 uppercase">
                    Present
                  </span>
                </div>
                <div className="text-gray-300">/</div>
                <div className="flex flex-col items-center px-2 py-1 bg-gray-50 rounded border border-gray-100">
                  <span className="font-bold text-gray-700">
                    {row.original.totaldays}
                  </span>
                  <span className="text-[9px] text-gray-500 uppercase">
                    Total
                  </span>
                </div>
              </div>
            ),
          },
        ]
      : []),
    {
      id: "actions",
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="outline"
          className={`h-8 text-xs gap-2 ${
            viewMode === "ASSIGNED"
              ? "hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
              : "hover:bg-green-50 hover:text-green-700 hover:border-green-200"
          }`}
          onClick={(e) => {
            e.stopPropagation();
            handleRowClick(row.original.id);
          }}
        >
          {viewMode === "ASSIGNED" ? (
            <>
              <ArrowsLeftRight size={14} /> Reassign
            </>
          ) : (
            <>
              <UserPlus size={14} /> Assign
            </>
          )}
        </Button>
      ),
    },
  ];

  const exportColumns = [
    { headerName: "ID", field: "code" },
    { headerName: "Intern Name", field: "fullName" },
    ...(viewMode === "UNASSIGNED"
      ? [
          { headerName: "Batch", field: "batch" },
          { headerName: "Duration", field: "duration" },
        ]
      : []),
    { headerName: "Course", field: "cos" },
    { headerName: "School", field: "school" },
    { headerName: "Department", field: "department" },
    { headerName: "Joined Date", field: "created_at" },
    ...(viewMode === "ASSIGNED"
      ? [
          { headerName: "Days Present", field: "attendance" },
          { headerName: "Expected Days", field: "totaldays" },
        ]
      : []),
  ];

  const tableData = useMemo(() => {
    const interns = data?.interns || [];
    return interns.map((intern) => {
      const durationVal =
        intern.application?.Applicant?.InternshipProgram?.[0]
          ?.duration_in_months;
      return {
        id: intern.id,
        code: intern.application?.code || "N/A",
        fullName: `${intern.first_name} ${intern.last_name}`,
        ittype:
          intern.application?.vacancy?.vacancy_position?.name?.toUpperCase() ||
          "",
        school:
          intern.application?.ApplicationsAcademicQualification?.institution
            ?.name ||
          intern.application?.ApplicationNYSCDetails?.institution?.name ||
          "",
        batch: intern.application?.vacancy?.batch?.name || "",
        duration: formatDuration(durationVal),
        department: getDepartmentName(intern),
        attendance: intern.MonthlyAttendances?.[0]?.days_present || 0,
        totaldays: intern.MonthlyAttendances?.[0]?.total_days || 0,
        cos:
          intern.application?.ApplicationsAcademicQualification?.course?.name ||
          intern.application?.ApplicationNYSCDetails?.course?.name ||
          "",
        created_at: intern.created_at,
      };
    });
  }, [data]);

  const table = useReactTable({
    data: tableData,
    columns,
    pageCount: data?.totalPages || -1,
    state: { pagination },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  if (isError)
    return (
      <div className="p-8 text-center text-red-500">
        An error occurred loading data.
      </div>
    );

  return (
    <>
      <Card className="w-full max-w-[95%] m-auto border-slate-200 shadow-sm bg-white mt-4">
        <CardHeader className="flex flex-col gap-4 p-4 border-b border-slate-100 bg-gray-50/40">
          <div className="flex flex-wrap items-center gap-4 border-b border-gray-200 pb-4">
            <button
              onClick={() => {
                setViewMode("UNASSIGNED");
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
              className={`text-sm font-medium px-4 py-2 rounded-md transition-colors ${
                viewMode === "UNASSIGNED"
                  ? "bg-green-100 text-green-700 shadow-sm"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              Unassigned Interns
            </button>
            <button
              onClick={() => {
                setViewMode("ASSIGNED");
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
              className={`text-sm font-medium px-4 py-2 rounded-md transition-colors ${
                viewMode === "ASSIGNED"
                  ? "bg-green-100 text-green-700 shadow-sm"
                  : "text-gray-500 hover:bg-gray-100"
              }`}
            >
              Deployed Interns
            </button>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <DateRangeFilter
                dateRange={dateRange}
                setDateRange={setDateRange}
                minDate={data?.meta?.minDate}
              />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs border-dashed border-gray-300"
                  >
                    <Funnel className="mr-2 h-3 w-3" />
                    Type: {selectedItType || "All"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-48 max-h-64 overflow-y-auto"
                >
                  <DropdownMenuItem onClick={() => setSelectedItType(null)}>
                    {!selectedItType && <Check className="mr-2 h-3 w-3" />} All
                    Types
                  </DropdownMenuItem>
                  {uniqueItTypes.map((type) => (
                    <DropdownMenuItem
                      key={type}
                      onClick={() => setSelectedItType(type)}
                    >
                      {selectedItType === type && (
                        <Check className="mr-2 h-3 w-3" />
                      )}{" "}
                      {type}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs border-dashed border-gray-300"
                  >
                    <CalendarBlank className="mr-2 h-3 w-3" />
                    Batch: {selectedBatch || "All"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-48 max-h-64 overflow-y-auto"
                >
                  <DropdownMenuItem onClick={() => setSelectedBatch(null)}>
                    {!selectedBatch && <Check className="mr-2 h-3 w-3" />} All
                    Batches
                  </DropdownMenuItem>
                  {uniqueBatches.map((batch) => (
                    <DropdownMenuItem
                      key={batch}
                      onClick={() => setSelectedBatch(batch)}
                    >
                      {selectedBatch === batch && (
                        <Check className="mr-2 h-3 w-3" />
                      )}{" "}
                      {batch}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              <div className="relative w-full sm:w-64">
                <MagnifyingGlass
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  size={14}
                />
                <Input
                  placeholder="Search interns..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearch}
                  className="pl-9 h-8 text-xs bg-white border-slate-200 focus:border-green-500 focus:ring-green-500"
                />
              </div>

              <ExportDropdown
                onExport={() => {}}
                data={tableData}
                columns={exportColumns}
                className="h-8"
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-gray-50/50">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow
                    key={headerGroup.id}
                    className="hover:bg-transparent border-b border-slate-100"
                  >
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className="h-9 text-[11px] font-bold text-gray-500 uppercase tracking-wider"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-64 text-center"
                    >
                      <div className="flex justify-center items-center w-full h-full">
                        <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className="hover:bg-green-50/30 transition-colors border-b border-slate-100 cursor-pointer group"
                      onClick={() => handleRowClick(row.original.id)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="py-3">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-32 text-center text-gray-500"
                    >
                      {viewMode === "UNASSIGNED"
                        ? "No unassigned interns found in your department."
                        : "No interns deployed yet."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>

        <div className="flex items-center justify-between p-2 px-4 border-t border-slate-100 bg-gray-50/30">
          <div className="text-[11px] text-gray-500">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="h-7 w-20 bg-white text-[11px]"
            >
              <CaretLeft className="mr-1" size={12} /> Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="h-7 w-20 bg-white text-[11px]"
            >
              Next <CaretRight className="ml-1" size={12} />
            </Button>
          </div>
        </div>
      </Card>

      <Sheet
        open={!!selectedInternId}
        onOpenChange={(open) => !open && setSelectedInternId(null)}
      >
        <SheetContent side="right" className="sm:max-w-[65%] w-full">
          {selectedInternId && (
            <AssignSupervisorViewer
              internId={selectedInternId}
              onClose={() => {
                setSelectedInternId(null);
                refetch();
              }}
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
